const normalizedFixtureId = (value) => (
  value == null ? null : String(value)
);

export const isSportsnewFixtureDetailGenerationCurrent = ({
  fixtureId,
  generation,
  detailFixtureIdRef,
  detailLifecycleGenerationRef,
} = {}) => (
  normalizedFixtureId(detailFixtureIdRef?.current) === normalizedFixtureId(fixtureId)
  && detailLifecycleGenerationRef?.current === generation
);

/**
 * Invalidates the active fixture-detail request without owning React state.
 *
 * Keeping the request/controller/RAF/perf cleanup here gives every close path
 * (manual close, operator suspension, 404/410, and a superseding click) the
 * same stale-response barrier. The caller remains responsible for clearing
 * rendered detail state when the invalidation represents a visible close.
 */
export const invalidateSportsnewFixtureDetailRequest = ({
  expectedFixtureId = null,
  detailAbortControllerRef,
  latestDetailRequestRef,
  detailFixtureIdRef,
  marketChunkRafRef,
  cancelFrame = typeof window === 'undefined' ? undefined : window.cancelAnimationFrame,
  clearClickMark = () => {},
} = {}) => {
  const latestFixtureId = latestDetailRequestRef?.current ?? null;
  const visibleFixtureId = detailFixtureIdRef?.current ?? null;
  const expectedId = normalizedFixtureId(expectedFixtureId);
  const activeIds = [latestFixtureId, visibleFixtureId]
    .map(normalizedFixtureId)
    .filter(Boolean);

  if (expectedId != null && !activeIds.includes(expectedId)) {
    return { invalidated: false, fixtureId: null };
  }

  const controller = detailAbortControllerRef?.current ?? null;
  const scheduledFrame = marketChunkRafRef?.current ?? null;
  const activeFixtureId = latestFixtureId ?? visibleFixtureId ?? expectedFixtureId ?? null;
  if (activeFixtureId == null && controller == null && scheduledFrame == null) {
    return { invalidated: false, fixtureId: null };
  }

  controller?.abort?.();
  if (detailAbortControllerRef) detailAbortControllerRef.current = null;
  if (latestDetailRequestRef) latestDetailRequestRef.current = null;
  if (detailFixtureIdRef) detailFixtureIdRef.current = null;

  if (scheduledFrame != null && typeof cancelFrame === 'function') {
    cancelFrame(scheduledFrame);
  }
  if (marketChunkRafRef) marketChunkRafRef.current = null;

  if (activeFixtureId != null) clearClickMark(activeFixtureId);
  return { invalidated: true, fixtureId: activeFixtureId };
};
